'use client';

import { useEffect, useRef, useState } from 'react';

import { ExternLogoFull } from '@extern/ui/logos';
import { CaretDownIcon, ListIcon } from '@phosphor-icons/react';
import Link from 'next/link';

import { cn } from '@/utils/cn';

function MenuContent() {
  return (
    <>
      <div className="group relative">
        <button
          className="flex cursor-pointer items-center gap-1 rounded-md px-3 py-2 font-medium text-[14px] transition-all duration-300 hover:rounded-md hover:bg-role-surface-container focus:bg-role-surface-container focus:outline-none"
          type="button">
          Students <CaretDownIcon className="h-5 w-5" />
        </button>
        <div className="z-10 max-h-0 overflow-hidden bg-role-surface transition-all duration-200 group-focus-within:max-h-60 md:invisible md:absolute md:top-full md:left-0 md:w-44 md:opacity-0 md:group-focus-within:visible md:group-focus-within:opacity-100">
          <div className="flex flex-col gap-1 p-2">
            <Link
              className="px-2 py-2 font-medium text-[14px] hover:rounded-md hover:bg-role-surface-container"
              href="https://extern.com/student">
              What we do
            </Link>
            <Link
              className="px-2 py-2 font-medium text-[14px] hover:rounded-md hover:bg-role-surface-container"
              href="https://extern.com/blog/student-blog">
              Student resources
            </Link>
            <Link
              className="px-2 py-2 font-medium text-[14px] hover:rounded-md hover:bg-role-surface-container"
              href="https://extern.com/externships">
              List of Externships
            </Link>
            <Link
              className="px-2 py-2 font-medium text-[14px] hover:rounded-md hover:bg-role-surface-container"
              href="https://extern.com/events">
              Community events
            </Link>
            <Link
              className="px-2 py-2 font-medium text-[14px] hover:rounded-md hover:bg-role-surface-container"
              href="https://app.extern.com/sign-up"
              target="_blank">
              Apply now
            </Link>
          </div>
        </div>
      </div>
      <Link
        className="cursor-pointer px-3 py-2 font-medium text-[14px] hover:rounded-md hover:bg-role-surface-container"
        href="https://extern.com/company">
        Companies
      </Link>
      <div className="group relative">
        <button
          className="flex cursor-pointer items-center gap-1 rounded-md px-3 py-2 font-medium text-[14px] transition-all duration-300 hover:rounded-md hover:bg-role-surface-container focus:bg-role-surface-container focus:outline-none"
          type="button">
          Resources <CaretDownIcon className="h-5 w-5" />
        </button>
        <div className="z-10 max-h-0 overflow-hidden bg-role-surface transition-all duration-200 group-focus-within:max-h-60 md:invisible md:absolute md:top-full md:left-0 md:w-48 md:opacity-0 md:group-focus-within:visible md:group-focus-within:opacity-100">
          <div className="flex flex-col gap-1 p-2">
            <Link
              className="cursor-pointer px-2 py-2 font-medium text-[14px] hover:rounded-md hover:bg-role-surface-container"
              href="https://extern.com/blog/student-blog">
              Community resources
            </Link>
            <Link
              className="cursor-pointer px-2 py-2 font-medium text-[14px] hover:rounded-md hover:bg-role-surface-container"
              href="https://extern.com/externships">
              List of Externships
            </Link>
            <Link
              className="cursor-pointer px-2 py-2 font-medium text-[14px] hover:rounded-md hover:bg-role-surface-container"
              href="https://app.extern.com/sign-up"
              target="_blank">
              Get Started
            </Link>
          </div>
        </div>
      </div>
      <Link
        className="cursor-pointer px-3 py-2 font-medium text-[14px] hover:rounded-md hover:bg-role-surface-container"
        href="https://extern.com/about">
        About
      </Link>
      <Link
        className="cursor-pointer px-3 py-2 font-medium text-[14px] hover:rounded-md hover:bg-role-surface-container"
        href="https://app.extern.com/sign-in"
        target="_blank">
        Login
      </Link>
      <Link
        className="hidden rounded-full bg-role-info px-4 py-2 font-medium text-[16px] text-role-on-info leading-[20px] transition-all duration-200 hover:rounded-full hover:bg-role-on-info-container md:block"
        href="https://app.extern.com/sign-up"
        target="_blank">
        Start today
      </Link>
    </>
  );
}

export function WebsiteAppbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <div className="relative w-full border-role-outline-subtle border-b bg-role-surface">
      <div className="relative z-10 mx-auto w-full bg-role-surface px-4 py-3 md:max-w-[876px] md:px-0">
        <div className="flex items-center justify-between">
          <ExternLogoFull className="w-[68px] md:w-[85px]" />
          <button
            className="mr-4 ml-auto block md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            ref={buttonRef}
            type="button">
            <ListIcon className="h-5 w-5" />
          </button>
          <div className="hidden items-center gap-4 md:flex">
            <MenuContent />
          </div>
          <Link
            className="block rounded-full bg-role-info px-4 py-2 font-medium text-[16px] text-role-on-info leading-[20px] transition-all duration-200 hover:rounded-full hover:bg-role-on-info-container md:hidden"
            href="https://app.extern.com/sign-up"
            target="_blank">
            Start today
          </Link>
        </div>
      </div>
      <div
        className={cn(
          'absolute top-full right-0 z-0 flex flex-col gap-4 bg-role-surface p-4 transition-all duration-300 md:hidden',
          isMenuOpen ? '-translate-y-0' : '-translate-y-full',
        )}
        ref={menuRef}>
        <MenuContent />
      </div>
    </div>
  );
}
